import React from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import { getFileIcon } from "../FileUpload";
import StatusMessage from "./StatusMessage";

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  id?: string;
  attachment?: Array<File>;
  createdAt?: Date;
}

// Smart LaTeX rendering function
const processContentForRendering = (content: string, isStreaming: boolean = false): string => {
  // If streaming and content is long, only process the last portion for performance
  if (isStreaming && content.length > 200) {
    const lastPortion = content.slice(-200);
    const beforeLastPortion = content.slice(0, -200);
    
    // Find the last complete LaTeX expression in the before portion
    const lastCompleteLatex = beforeLastPortion.lastIndexOf('$');
    const beforeCompleteLatex = lastCompleteLatex > 0 ? beforeLastPortion.slice(0, lastCompleteLatex + 1) : beforeLastPortion;
    
    // Process LaTeX fixes only on the combined content we'll actually render
    const contentToProcess = beforeCompleteLatex + lastPortion;
    return processLatexDelimiters(contentToProcess);
  }
  
  // For complete messages or short content, process everything
  return processLatexDelimiters(content);
};

// Convert various LaTeX delimiter formats to $ delimiters for remark-math
const processLatexDelimiters = (content: string): string => {
  return content
    // Fix double-escaped delimiters first
    .replace(/\\\\\(/g, '\\(')
    .replace(/\\\\\)/g, '\\)')
    .replace(/\\\\\[/g, '\\[')
    .replace(/\\\\\]/g, '\\]')
    // Convert \( \) to $ $ for inline math
    .replace(/\\\(([^)]+)\\\)/g, '$$$1$$')
    // Convert \[ \] to $$ $$ for display math
    .replace(/\\\[([^\]]+)\\\]/g, '$$$$$$1$$$$');
};

// Check if content likely contains LaTeX that needs processing
const hasLatexContent = (content: string): boolean => {
  return content.includes('$') || content.includes('\\(') || content.includes('\\[');
};


// Memoized component for rendering individual messages
export const MessageItem = React.memo(({ 
  message, 
  index
}: { 
  message: ChatMessage, 
  index: number
}) => {
 if (!(message.role === "user" || (message.role === "assistant" && message.content.trim()))) {
    return null;
  }

  // Determine if this is likely a streaming message (no createdAt means it's still being built)
  const isStreaming = message.role === 'assistant' && !message.createdAt;
  
  // Use smart content processing
  const processedContent = React.useMemo(() => {
    return processContentForRendering(message.content, isStreaming);
  }, [message.content, isStreaming]);

  // Only use LaTeX plugins if content actually has LaTeX
  const shouldUseLaTeX = React.useMemo(() => {
    return hasLatexContent(message.content);
  }, [message.content]);

  return (
    <div
      key={message.id ?? index}
      className={`flex ${message.role === "user" ? "justify-end" : "justify-start items-start"} min-w-0`}
    >
      <div
        className={`break-words rounded-lg px-3 py-2 text-sm overflow-hidden min-w-0 ${
          message.role === "user"
            ? "max-w-[70%] text-white user-message"
            : "flex-1 bg-transparent text-gray-900 assistant-message"
        }`}
        style={message.role === "user" ? { backgroundColor: "#1e9cd7" } : {}}
      >
        {message.attachment && message.attachment.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1">
            {message.attachment.map((file, idx) => (
              <span key={idx} className="flex items-center px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs mr-1">
                {getFileIcon(file)} {file.name}
              </span>
            ))}
          </div>
        )}
        
        {message.content.includes('[File Information]') ? (
          (() => {
            const lines = message.content.split('\n');
            const fileInfo = lines.find((line: string) => line.includes('Original name:'));
            const fileName = fileInfo 
              ? fileInfo.split('Original name:')[1].trim() 
              : null;
            
            return (
              <div className="flex flex-col gap-1">
                <div className="text-sm">{message.content}</div>
                {fileName && <div className="text-xs text-gray-500">📎 {fileName}</div>}
              </div>
            );
          })()
        ) : (
          <div className="markdown-content min-w-0 max-w-full" style={{ maxWidth: '100%', overflowX: 'hidden', contain: 'layout style' }}>
            <ReactMarkdown 
              remarkPlugins={shouldUseLaTeX ? [remarkGfm, remarkMath] : [remarkGfm]}
              rehypePlugins={shouldUseLaTeX ? [rehypeKatex] : []}
              components={{
                // Custom component to handle math blocks
                div: ({ className, children, ...props }) => {
                  if (className?.includes('math')) {
                    return (
                      <div 
                        className={className} 
                        style={{ 
                          maxWidth: '100%',
                          contain: 'layout style' 
                        }}
                        {...props}
                      >
                        {children}
                      </div>
                    );
                  }
                  return <div className={className} {...props}>{children}</div>;
                }
              }}
            >
              {processedContent}
            </ReactMarkdown>
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for memoization
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.index === nextProps.index
  );
});

MessageItem.displayName = 'MessageItem';


// Helper component for messages container
export default function MessagesContainer({ 
  messages, 
  messagesHeightClass, 
  bottomRef,
  agentStatus
}: {
  messages: ChatMessage[],
  messagesHeightClass?: string,
  bottomRef: React.RefObject<HTMLDivElement | null>,
  agentStatus?: string | null
}) {
  return (
    <div className={`${messagesHeightClass || "h-36"} overflow-y-auto overflow-x-hidden px-4 py-2 space-y-2 min-w-0 max-w-full`}>
      {messages.map((m, i) => (
        <MessageItem 
          key={m.id ?? i} 
          message={m} 
          index={i} 
        />
      ))}
      {agentStatus !== null && (
        <StatusMessage status={agentStatus} />
      )}
      <div ref={bottomRef} />
    </div>
  );
}